/**
 * @module PackRewards
 * @description "Récompenses de Meute" — Displays pack win streak,
 * tier progression (Bronze/Silver/Gold), and unlocked rewards.
 * For members on Community page.
 */
import React, { useState, useEffect, memo } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Gift, Trophy, Lock, Unlock, Play, ExternalLink, Tag,
  MessageSquare, Flame, Loader2, Star, Medal, Crown
} from 'lucide-react';
import { motion } from 'framer-motion';

const TIER_CONFIG = {
  bronze: { icon: Medal, color: 'text-amber-600', bg: 'bg-amber-600/10', border: 'border-amber-600/30', gradient: 'from-amber-700 to-amber-500' },
  silver: { icon: Medal, color: 'text-gray-300', bg: 'bg-gray-400/10', border: 'border-gray-400/30', gradient: 'from-gray-400 to-gray-200' },
  gold:   { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', gradient: 'from-yellow-500 to-amber-300' },
};

const REWARD_TYPE_ICONS = {
  message: MessageSquare,
  video: Play,
  promo: Tag,
  custom: Gift,
};

const PackRewards = memo(function PackRewards() {
  const { language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fr = language === 'fr';

  useEffect(() => {
    api.get('/challenges/pack-rewards/status')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="bg-card/50 border-border animate-pulse">
        <CardContent className="p-6 h-24 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const { streak, unlocked_tiers, next_tier, rewards, tiers } = data;
  const tierOrder = ['bronze', 'silver', 'gold'];

  // Filter rewards that are accessible (tier is unlocked)
  const accessibleRewards = (rewards || []).filter(r => unlocked_tiers.includes(r.tier));
  const lockedRewards = (rewards || []).filter(r => !unlocked_tiers.includes(r.tier));

  return (
    <Card className="bg-card/50 border-border overflow-hidden" data-testid="pack-rewards">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
          <Gift className="w-5 h-5 text-primary" />
          {fr ? 'Recompenses d\'equipe' : 'Team Rewards'}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {fr
            ? `${streak} victoire(s) consecutive(s) de l'equipe`
            : `${streak} consecutive pack win(s)`}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Tier progression */}
        <div className="flex items-center gap-2" data-testid="pack-rewards-tiers">
          {tierOrder.map((tierKey, i) => {
            const tier = tiers[tierKey];
            const cfg = TIER_CONFIG[tierKey];
            const TierIcon = cfg.icon;
            const isUnlocked = unlocked_tiers.includes(tierKey);
            const isNext = next_tier?.key === tierKey;

            return (
              <React.Fragment key={tierKey}>
                {i > 0 && (
                  <div className={`flex-1 h-1 rounded-full ${isUnlocked ? `bg-gradient-to-r ${cfg.gradient}` : 'bg-secondary'}`} />
                )}
                <div className="flex flex-col items-center gap-1">
                  <motion.div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 transition-all ${
                      isUnlocked
                        ? `${cfg.bg} ${cfg.border} ${cfg.color}`
                        : 'bg-secondary/50 border-border text-muted-foreground'
                    }`}
                    animate={isUnlocked ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.5, delay: i * 0.2 }}
                  >
                    {isUnlocked ? <TierIcon className="w-5 h-5" /> : <Lock className="w-4 h-4" />}
                  </motion.div>
                  <span className={`text-[9px] font-heading uppercase ${isUnlocked ? cfg.color : 'text-muted-foreground'}`}>
                    {fr ? tier.label_fr : tier.label_en}
                  </span>
                  <span className="text-[8px] text-muted-foreground">{tier.wins_required} {fr ? 'vict.' : 'wins'}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* Next tier info */}
        {next_tier && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-secondary/40 text-xs">
            <Flame className="w-4 h-4 text-orange-400 shrink-0" />
            <span className="text-muted-foreground">
              {fr
                ? `Plus que ${next_tier.wins_remaining} victoire(s) pour le palier ${next_tier.label_fr}!`
                : `${next_tier.wins_remaining} more win(s) to unlock ${next_tier.label_en}!`}
            </span>
          </div>
        )}

        {/* Unlocked rewards */}
        {accessibleRewards.length > 0 && (
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Unlock className="w-3 h-3" /> {fr ? 'Recompenses Debloquees' : 'Unlocked Rewards'}
            </p>
            {accessibleRewards.map(r => {
              const cfg = TIER_CONFIG[r.tier] || TIER_CONFIG.bronze;
              const TypeIcon = REWARD_TYPE_ICONS[r.reward_type] || Gift;
              return (
                <motion.div
                  key={r.reward_id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-xl ${cfg.bg} border ${cfg.border}`}
                  data-testid={`reward-${r.reward_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-background/50`}>
                      <TypeIcon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-bold ${cfg.color}`}>{r.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>
                      {r.content_url && (
                        <a
                          href={r.content_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-1 mt-1.5 text-xs font-medium ${cfg.color} hover:underline`}
                        >
                          {r.reward_type === 'video' ? (fr ? 'Voir la video' : 'Watch video') : (fr ? 'Acceder' : 'Access')}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    <Star className={`w-4 h-4 ${cfg.color} shrink-0`} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Locked rewards (teaser) */}
        {lockedRewards.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Lock className="w-3 h-3" /> {fr ? 'A Débloquer' : 'To Unlock'}
            </p>
            {lockedRewards.map(r => {
              const tierInfo = tiers[r.tier] || {};
              return (
                <div key={r.reward_id} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30 opacity-60">
                  <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground truncate">{r.title}</span>
                  <span className="ml-auto text-[9px] text-muted-foreground shrink-0">
                    {tierInfo.wins_required || '?'} {fr ? 'vict.' : 'wins'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty state */}
        {(!rewards || rewards.length === 0) && streak === 0 && (
          <p className="text-xs text-center text-muted-foreground py-2">
            {fr
              ? 'Gagnez des défis pour débloquer des récompenses exclusives!'
              : 'Win challenges to unlock exclusive rewards!'}
          </p>
        )}
      </CardContent>
    </Card>
  );
});

export default PackRewards;
